import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, LayoutGrid, Columns3 } from "lucide-react";
import { EVENT_TYPES, SERVICE_EDIT_ROLES, ServiceStatusBadge } from "@/components/services/serviceMeta";
import { buildMonthGrid, eventsByDay, DayCell } from "@/components/services/MonthGrid";
import { ScheduleBoard } from "@/components/services/ScheduleBoard";
import { buildColumns, calendarRange, stepAnchor, rangeLabel } from "@/components/services/scheduleRange";
import { ServiceJobDialog } from "@/components/services/ServiceJobDialog";
import { fmtDate, localToday } from "@/components/equipment/equipmentMeta";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LINK = { service: "/service-jobs", hire: "/equipment?tab=orders", return: "/equipment?tab=orders", due: "/equipment?due=soon" };
const seg = (on) => `px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 transition-colors ${on ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"}`;

const DayPanel = ({ date, items, canCreate, onAdd }) => (
  <aside className="bg-white border border-zinc-200 shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-4 self-start" data-testid="schedule-day-panel">
    <div className="flex items-center justify-between mb-3">
      <div className="font-heading text-lg font-bold">{fmtDate(date)}</div>
      {canCreate && <button onClick={onAdd} data-testid="schedule-add-service" className="text-xs font-bold text-brand flex items-center gap-1 hover:underline"><Plus size={14} /> Service job</button>}
    </div>
    {items.length === 0 ? <p className="text-sm text-zinc-400">Nothing scheduled.</p> : (
      <ul className="space-y-2">
        {items.map((e, i) => (
          <li key={`${e.type}-${e.id}-${i}`} data-testid={`schedule-item-${e.type}-${e.id}`}>
            <Link to={LINK[e.type]} className="block border border-zinc-200 hover:border-brand p-2.5 transition-colors">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                <span className={`w-2 h-2 rounded-full ${EVENT_TYPES[e.type].dot}`} /> {EVENT_TYPES[e.type].label.replace(/s$/, "")}
                {e.time && <span className="font-mono text-zinc-700">{e.time}</span>}
              </div>
              <div className="text-sm font-bold text-zinc-900 mt-0.5 break-words">{e.title}</div>
              <div className="text-xs text-zinc-600 break-words">{e.detail}{e.type === "service" && e.customer ? ` · ${e.customer}` : ""}</div>
              {e.type === "hire" && <div className="text-[11px] text-zinc-400">{fmtDate(e.start)} → {fmtDate(e.end)}</div>}
              {e.type === "service" && <div className="mt-1"><ServiceStatusBadge status={e.status} /></div>}
            </Link>
          </li>
        ))}
      </ul>
    )}
  </aside>
);

export default function Schedule() {
  const { user } = useAuth();
  const isTech = user.role === "service_technician" || (user.role === "rigger" && user.dual_role);
  const canCreate = SERVICE_EDIT_ROLES.includes(user.role);
  const today = localToday();
  const [view, setView] = useState("board");
  const [scale, setScale] = useState("month");
  const [anchor, setAnchor] = useState(today);
  const [events, setEvents] = useState([]);
  const [shown, setShown] = useState({ service: true, hire: true, return: true, due: true });
  const [selected, setSelected] = useState(today);
  const [open, setOpen] = useState(false);
  const [techs, setTechs] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const columns = useMemo(() => buildColumns(scale, anchor), [scale, anchor]);
  const range = useMemo(() => (view === "calendar" ? calendarRange(anchor) : { start: columns[0].start, end: columns[columns.length - 1].end }), [view, anchor, columns]);

  const load = useCallback(() => api.get(`/schedule?start=${range.start}&end=${range.end}`).then((r) => setEvents(r.data.events)).catch(() => {}), [range.start, range.end]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canCreate) return;
    api.get("/service-technicians").then((r) => setTechs(r.data)).catch(() => {});
    api.get("/equipment").then((r) => setEquipment(r.data)).catch(() => {});
  }, [canCreate]);

  const move = async (id, date) => {
    setEvents((list) => list.map((e) => (e.type === "service" && e.id === id ? { ...e, date } : e)));
    try {
      await api.put(`/service-jobs/${id}`, { scheduled_date: date });
      toast.success(`Rescheduled to ${fmtDate(date)}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    load();
  };

  const go = (dir) => {
    const a = stepAnchor(view, scale, anchor, dir);
    setAnchor(a);
    setSelected(today.startsWith(a.slice(0, 7)) ? today : `${a.slice(0, 7)}-01`);
  };

  const visible = useMemo(() => events.filter((e) => shown[e.type]), [events, shown]);
  const byDay = useMemo(() => eventsByDay(visible, range.start, range.end), [visible, range]);
  const grid = useMemo(() => buildMonthGrid(anchor.slice(0, 7)), [anchor]);
  const types = isTech ? ["service"] : Object.keys(EVENT_TYPES);
  const canDrag = canCreate;

  return (
    <div className="p-6 sm:p-8 max-w-[1600px] mx-auto" data-testid="schedule-page">
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display">{isTech ? "My Schedule" : "Jobs & Services Schedule"}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-zinc-300" data-testid="schedule-view-toggle">
            <button onClick={() => setView("board")} data-testid="schedule-view-board" className={seg(view === "board")}><Columns3 size={14} /> Board</button>
            <button onClick={() => setView("calendar")} data-testid="schedule-view-calendar" className={seg(view === "calendar")}><LayoutGrid size={14} /> Calendar</button>
          </div>
          {view === "board" && (
            <div className="flex border border-zinc-300" data-testid="schedule-scale-toggle">
              {["day", "week", "month"].map((s) => (
                <button key={s} onClick={() => setScale(s)} data-testid={`schedule-scale-${s}`} className={seg(scale === s)}>{s[0].toUpperCase() + s.slice(1)}</button>
              ))}
            </div>
          )}
          <button onClick={() => go(-1)} data-testid="schedule-prev" className="p-2 border border-zinc-300 bg-white hover:border-zinc-900"><ChevronLeft size={18} /></button>
          <div className="font-heading text-lg font-bold min-w-[170px] text-center" data-testid="schedule-month-label">{rangeLabel(view, scale, anchor)}</div>
          <button onClick={() => go(1)} data-testid="schedule-next" className="p-2 border border-zinc-300 bg-white hover:border-zinc-900"><ChevronRight size={18} /></button>
          <button onClick={() => { setAnchor(today); setSelected(today); }} data-testid="schedule-today" className="px-3 py-2 text-sm font-bold border border-zinc-300 bg-white hover:border-zinc-900">Today</button>
          {canCreate && view === "board" && (
            <button onClick={() => { setSelected(today); setOpen(true); }} data-testid="schedule-new-service" className="bg-brand text-white rounded-full font-bold px-4 py-2 text-sm flex items-center gap-1.5 hover:bg-brand-dark"><Plus size={16} /> Service job</button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="schedule-legend">
        {types.map((t) => (
          <button key={t} onClick={() => setShown({ ...shown, [t]: !shown[t] })} data-testid={`schedule-toggle-${t}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border flex items-center gap-2 transition-opacity ${shown[t] ? "bg-white border-zinc-300" : "bg-zinc-100 border-zinc-200 opacity-50"}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${EVENT_TYPES[t].dot}`} /> {EVENT_TYPES[t].label}
            <span className="font-mono text-[10px] text-zinc-500">{events.filter((e) => e.type === t).length}</span>
          </button>
        ))}
        {canDrag && <span className="text-xs text-zinc-500 ml-2">Drag a service job to reschedule it.</span>}
      </div>
      {view === "board" ? (
        <ScheduleBoard columns={columns} events={visible} canDrag={canDrag} scale={scale} today={today} onMove={move} />
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="overflow-x-auto">
            <div className="min-w-[720px] border-l border-t border-zinc-200" data-testid="schedule-grid">
              <div className="grid grid-cols-7 bg-zinc-900 text-white">
                {WEEKDAYS.map((d) => <div key={d} className="px-2 py-2 text-[11px] font-bold uppercase tracking-wider">{d}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {grid.map((day) => (
                  <DayCell key={day.key} day={day} items={byDay[day.key] || []} selected={selected === day.key} isToday={day.key === today}
                    onSelect={setSelected} canDrag={canDrag} onMove={move} />
                ))}
              </div>
            </div>
          </div>
          <DayPanel date={selected} items={byDay[selected] || []} canCreate={canCreate} onAdd={() => setOpen(true)} />
        </div>
      )}
      {canCreate && <ServiceJobDialog open={open} onOpenChange={setOpen} editing={null} technicians={techs} equipment={equipment} onSaved={load} defaultDate={selected} />}
    </div>
  );
}
